<script setup>
import { computed, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { useElementSize } from '@vueuse/core';
import BackButton from '../BackButton.vue';
import InboxName from '../InboxName.vue';
import MoreActions from './MoreActions.vue';
import Avatar from 'next/avatar/Avatar.vue';
import SLACardLabel from './components/SLACardLabel.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ScheduleMessageDialog from './ScheduleMessageDialog.vue';
import wootConstants from 'dashboard/constants/globals';
import { conversationListPageURL } from 'dashboard/helper/URLHelper';
import { snoozedReopenTime } from 'dashboard/helper/snoozeHelpers';
import { useInbox } from 'dashboard/composables/useInbox';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import PipelinesApi from 'dashboard/api/pipelines';
import ContactAPI from 'dashboard/api/contacts';
import ConversationAiApi from 'dashboard/api/conversationAi';

const props = defineProps({
  chat: {
    type: Object,
    default: () => ({}),
  },
  showBackButton: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();
const store = useStore();
const route = useRoute();
const conversationHeader = ref(null);
const { width } = useElementSize(conversationHeader);
const { isAWebWidgetInbox } = useInbox();

const currentChat = computed(() => store.getters.getSelectedChat);
const accountId = computed(() => store.getters.getCurrentAccountId);

const chatMetadata = computed(() => props.chat.meta);

const backButtonUrl = computed(() => {
  const {
    params: { inbox_id: inboxId, label, teamId, id: customViewId },
    name,
  } = route;

  const conversationTypeMap = {
    conversation_through_mentions: 'mention',
    conversation_through_unattended: 'unattended',
  };
  return conversationListPageURL({
    accountId: accountId.value,
    inboxId,
    label,
    teamId,
    conversationType: conversationTypeMap[name],
    customViewId,
  });
});

const isHMACVerified = computed(() => {
  if (!isAWebWidgetInbox.value) {
    return true;
  }
  return chatMetadata.value.hmac_verified;
});

const currentContact = computed(() => {
  const metaSender = props.chat.meta?.sender || {};
  const storedContact = store.getters['contacts/getContact'](metaSender.id);

  return {
    ...metaSender,
    ...storedContact,
    thumbnail: storedContact.thumbnail || metaSender.thumbnail || '',
  };
});

const isSnoozed = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.SNOOZED
);

const snoozedDisplayText = computed(() => {
  const { snoozed_until: snoozedUntil } = currentChat.value;
  if (snoozedUntil) {
    return `${t('CONVERSATION.HEADER.SNOOZED_UNTIL')} ${snoozedReopenTime(snoozedUntil)}`;
  }
  return t('CONVERSATION.HEADER.SNOOZED_UNTIL_NEXT_REPLY');
});

const inbox = computed(() => {
  const { inbox_id: inboxId } = props.chat;
  return store.getters['inboxes/getInbox'](inboxId);
});

const hasMultipleInboxes = computed(
  () => store.getters['inboxes/getInboxes'].length > 1
);

const hasSlaPolicyId = computed(() => props.chat?.sla_policy_id);
const contactId = computed(() => props.chat?.meta?.sender?.id);
const hasContact = computed(() => !!contactId.value);
const hasPauseSupabaseTableConfigured = computed(() => {
  const tables = inbox.value?.supabase_config?.tables || [];
  return tables.some(table =>
    table?.name?.toString().startsWith('sistema_pause_')
  );
});
const showAiToggle = computed(
  () =>
    hasPauseSupabaseTableConfigured.value ||
    inbox.value?.has_master_ai_agent === true
);

const quickPipelines = ref([]);
const quickStages = ref([]);
const selectedPipelineId = ref('');
const selectedStageId = ref('');
const pipelineMemberships = ref([]);
const membershipStageSelection = ref({});
const isPipelineManagerOpen = ref(false);
const isLoadingPipelines = ref(false);
const isLoadingStages = ref(false);
const isLoadingMemberships = ref(false);
const isAddingToPipeline = ref(false);
const movingMembershipId = ref(null);
const removingMembershipId = ref(null);
const pendingRemoveMembership = ref(null);
const deleteMembershipDialogRef = ref(null);
const scheduleDialogRef = ref(null);
const isAiPaused = ref(false);
const isTogglingAi = ref(false);

const hasMemberships = computed(() => pipelineMemberships.value.length > 0);

const hydrateMembershipStageSelection = () => {
  membershipStageSelection.value = pipelineMemberships.value.reduce(
    (acc, membership) => ({
      ...acc,
      [membership.id]: membership.pipeline_stage_id,
    }),
    {}
  );
};

const loadQuickStages = async pipelineId => {
  if (!pipelineId) {
    quickStages.value = [];
    selectedStageId.value = '';
    return;
  }

  const selectedPipeline = quickPipelines.value.find(
    pipeline => pipeline.id === pipelineId
  );

  if (selectedPipeline?.stages?.length) {
    quickStages.value = selectedPipeline.stages;
    selectedStageId.value = selectedPipeline.stages[0]?.id || '';
    return;
  }

  isLoadingStages.value = true;
  try {
    const response = await PipelinesApi.listStages(pipelineId);
    quickStages.value = response.data.payload || [];
    selectedStageId.value = quickStages.value[0]?.id || '';
  } catch {
    quickStages.value = [];
    selectedStageId.value = '';
    useAlert(t('CONVERSATION.HEADER.KANBAN.ERR_LOAD_STAGES'));
  } finally {
    isLoadingStages.value = false;
  }
};

const loadMemberships = async () => {
  if (!hasContact.value) return;

  isLoadingMemberships.value = true;
  try {
    const response = await ContactAPI.getPipelineMemberships(contactId.value);
    const payload = response.data.payload || {};

    quickPipelines.value = payload.available_pipelines || [];
    pipelineMemberships.value = payload.memberships || [];

    if (!selectedPipelineId.value) {
      selectedPipelineId.value = quickPipelines.value[0]?.id || '';
    }
    await loadQuickStages(selectedPipelineId.value);
    hydrateMembershipStageSelection();
  } catch {
    useAlert(t('CONVERSATION.HEADER.KANBAN.ERR_LOAD_PIPELINES'));
  } finally {
    isLoadingMemberships.value = false;
  }
};

const openPipelineManager = async () => {
  if (!hasContact.value) return;

  isLoadingPipelines.value = true;
  try {
    await loadMemberships();
    isPipelineManagerOpen.value = true;
  } finally {
    isLoadingPipelines.value = false;
  }
};

const closePipelineManager = () => {
  isPipelineManagerOpen.value = false;
};

const addContactToPipeline = async () => {
  if (!selectedPipelineId.value || !contactId.value) return;

  isAddingToPipeline.value = true;
  try {
    await PipelinesApi.addItem(selectedPipelineId.value, {
      item_type: 'Contact',
      item_id: contactId.value,
      pipeline_stage_id: selectedStageId.value || undefined,
    });
    await loadMemberships();
    useAlert(t('CONVERSATION.HEADER.KANBAN.SUCCESS'));
  } catch (error) {
    useAlert(
      error?.response?.data?.error || t('CONVERSATION.HEADER.KANBAN.ERR_ADD')
    );
  } finally {
    isAddingToPipeline.value = false;
  }
};

const moveMembership = async membership => {
  const toStageId = membershipStageSelection.value[membership.id];
  if (!toStageId || membership.pipeline_stage_id === toStageId) return;

  movingMembershipId.value = membership.id;
  try {
    await PipelinesApi.moveItem(membership.pipeline_id, membership.id, {
      to_stage_id: toStageId,
    });
    await loadMemberships();
    useAlert(t('CONVERSATION.HEADER.KANBAN.SUCCESS_MOVE'));
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('CONVERSATION.HEADER.KANBAN.ERR_MOVE_STAGE')
    );
  } finally {
    movingMembershipId.value = null;
  }
};

const requestRemoveMembership = membership => {
  pendingRemoveMembership.value = membership;
  deleteMembershipDialogRef.value?.open();
};

const removeMembership = async () => {
  if (!pendingRemoveMembership.value) return;

  const membership = pendingRemoveMembership.value;
  removingMembershipId.value = membership.id;
  try {
    await PipelinesApi.removeItem(membership.pipeline_id, membership.id);
    await loadMemberships();
    useAlert(t('CONVERSATION.HEADER.KANBAN.SUCCESS_REMOVE'));
  } catch {
    useAlert(t('CONVERSATION.HEADER.KANBAN.ERR_REMOVE'));
  } finally {
    removingMembershipId.value = null;
    pendingRemoveMembership.value = null;
    deleteMembershipDialogRef.value?.close();
  }
};

const openScheduleDialog = () => {
  scheduleDialogRef.value?.open();
};

const loadAiStatus = async () => {
  const id = currentChat.value?.id;
  if (!id || !showAiToggle.value) {
    isAiPaused.value = false;
    return;
  }
  try {
    const { data } = await ConversationAiApi.getAiStatus(id);
    isAiPaused.value = data.paused === true;
  } catch {
    isAiPaused.value = false;
  }
};

const toggleAiPause = async () => {
  if (!showAiToggle.value) return;
  isTogglingAi.value = true;
  const newPausedState = !isAiPaused.value;
  try {
    await ConversationAiApi.toggleAi(
      currentChat.value.id,
      newPausedState ? 'pause' : 'resume'
    );
    await loadAiStatus();
  } catch (err) {
    useAlert(
      err?.response?.data?.error || t('CONVERSATION.HEADER.PAUSE_AI.ERROR')
    );
  } finally {
    isTogglingAi.value = false;
  }
};

watch(
  [() => currentChat.value?.id, showAiToggle],
  async ([newId]) => {
    if (newId && showAiToggle.value) {
      await loadAiStatus();
    } else {
      isAiPaused.value = false;
    }
  },
  { immediate: true }
);

watch(selectedPipelineId, async pipelineId => {
  await loadQuickStages(pipelineId);
});
</script>

<template>
  <div
    ref="conversationHeader"
    class="flex flex-col gap-3 items-center justify-between flex-1 w-full min-w-0 xl:flex-row px-3 pt-3 pb-2 h-24 xl:h-12"
  >
    <div
      class="flex items-center justify-start w-full xl:w-auto max-w-full min-w-0 xl:flex-1"
    >
      <BackButton
        v-if="showBackButton"
        :back-url="backButtonUrl"
        class="ltr:mr-2 rtl:ml-2"
      />
      <Avatar
        :name="currentContact.name"
        :src="currentContact.thumbnail"
        :size="32"
        :status="currentContact.availability_status"
        hide-offline-status
        rounded-full
      />
      <div
        class="flex flex-col items-start min-w-0 ml-2 overflow-hidden rtl:ml-0 rtl:mr-2"
      >
        <div class="flex flex-row items-center max-w-full gap-1 p-0 m-0">
          <span
            class="text-sm font-medium truncate leading-tight text-n-slate-12"
          >
            {{ currentContact.name }}
          </span>
          <fluent-icon
            v-if="!isHMACVerified"
            v-tooltip="$t('CONVERSATION.UNVERIFIED_SESSION')"
            size="14"
            class="text-n-amber-10 my-0 mx-0 min-w-[14px] flex-shrink-0"
            icon="warning"
          />
        </div>

        <div
          class="flex items-center gap-2 overflow-hidden text-xs conversation--header--actions text-ellipsis whitespace-nowrap"
        >
          <InboxName v-if="hasMultipleInboxes" :inbox="inbox" class="!mx-0" />
          <span v-if="isSnoozed" class="font-medium text-n-amber-10">
            {{ snoozedDisplayText }}
          </span>
        </div>
      </div>
    </div>
    <div
      class="flex flex-row items-center justify-start xl:justify-end flex-shrink-0 gap-2 w-full xl:w-auto header-actions-wrap"
    >
      <button
        v-if="showAiToggle"
        type="button"
        :disabled="isTogglingAi"
        class="flex items-center gap-2 rounded-full border px-2 py-1 text-xs font-medium transition-colors"
        :class="
          isAiPaused
            ? 'border-n-ruby-7 bg-n-ruby-3 text-n-ruby-11'
            : 'border-n-teal-7 bg-n-teal-3 text-n-teal-11'
        "
        @click="toggleAiPause"
      >
        <span
          class="relative inline-flex h-4 w-7 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none"
          :class="isAiPaused ? 'bg-n-ruby-9' : 'bg-n-teal-9'"
        >
          <span
            class="pointer-events-none inline-block h-3 w-3 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out"
            :class="isAiPaused ? 'translate-x-3' : 'translate-x-0'"
          />
        </span>
        <span>
          {{
            isAiPaused
              ? t('CONVERSATION.HEADER.PAUSE_AI.PAUSED')
              : t('CONVERSATION.HEADER.PAUSE_AI.ACTIVE')
          }}
        </span>
      </button>
      <Button
        v-if="hasContact"
        size="xs"
        color="teal"
        variant="outline"
        icon="i-lucide-workflow"
        :label="t('CONVERSATION.HEADER.KANBAN.CTA')"
        :is-loading="isLoadingPipelines"
        @click="openPipelineManager"
      />
      <Button
        size="xs"
        color="blue"
        variant="outline"
        icon="i-lucide-calendar-clock"
        :label="t('CONVERSATION.SCHEDULED_MESSAGE.ACTIONS.OPEN')"
        @click="openScheduleDialog"
      />
      <SLACardLabel
        v-if="hasSlaPolicyId"
        :chat="chat"
        show-extended-info
        :parent-width="width"
        class="hidden md:flex"
      />
      <MoreActions :conversation-id="currentChat.id" />
    </div>

    <div
      v-if="isPipelineManagerOpen"
      class="fixed inset-0 z-[1300] bg-n-alpha-black1/70 backdrop-blur-sm"
    >
      <div class="h-full w-full overflow-y-auto p-4 md:p-6">
        <div
          class="mx-auto flex min-h-full w-full max-w-[1500px] flex-col rounded-2xl border border-n-alpha-3 bg-n-solid-1 shadow-xl"
        >
          <header
            class="sticky top-0 z-10 flex items-start justify-between border-b border-n-alpha-3 bg-n-solid-1/95 px-4 py-3 backdrop-blur-sm"
          >
            <div>
              <h2 class="text-lg font-semibold text-n-slate-12">
                {{ t('CONVERSATION.HEADER.KANBAN.TITLE') }}
              </h2>
              <p class="text-sm text-n-slate-11">
                {{ t('CONVERSATION.HEADER.KANBAN.DESCRIPTION') }}
              </p>
            </div>
            <button
              type="button"
              class="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-n-alpha-3 text-n-slate-11 transition hover:border-n-alpha-5 hover:text-n-slate-12"
              :aria-label="t('CONVERSATION.HEADER.CLOSE')"
              @click="closePipelineManager"
            >
              <span class="i-lucide-x text-lg" />
            </button>
          </header>

          <div
            class="grid flex-1 grid-cols-1 gap-4 overflow-hidden p-4 md:grid-cols-12"
          >
            <aside class="space-y-3 md:col-span-4">
              <div class="rounded-xl border border-n-alpha-3 bg-n-alpha-1 p-3">
                <p class="text-xs text-n-slate-10">
                  {{ t('CONVERSATION.HEADER.KANBAN.ACTIVE_MEMBERSHIPS') }}
                </p>
                <p class="text-lg font-semibold text-n-slate-12">
                  {{ pipelineMemberships.length }}
                </p>
              </div>

              <div class="rounded-xl border border-n-alpha-3 bg-n-alpha-1 p-3">
                <p class="mb-3 text-sm font-semibold text-n-slate-12">
                  {{ t('CONVERSATION.HEADER.KANBAN.QUICK_ADD') }}
                </p>
                <div class="space-y-3">
                  <div>
                    <label class="text-sm font-medium text-n-slate-12">
                      {{ t('CONVERSATION.HEADER.KANBAN.PIPELINE') }}
                    </label>
                    <select
                      v-model="selectedPipelineId"
                      class="mt-1 w-full rounded-lg border border-n-alpha-3 bg-n-background px-3 py-2 text-sm text-n-slate-12"
                    >
                      <option value="">
                        {{ t('CONVERSATION.HEADER.KANBAN.SELECT_PIPELINE') }}
                      </option>
                      <option
                        v-for="pipeline in quickPipelines"
                        :key="pipeline.id"
                        :value="pipeline.id"
                      >
                        {{ pipeline.name }}
                      </option>
                    </select>
                  </div>

                  <div>
                    <label class="text-sm font-medium text-n-slate-12">
                      {{ t('CONVERSATION.HEADER.KANBAN.STAGE') }}
                    </label>
                    <select
                      v-model="selectedStageId"
                      class="mt-1 w-full rounded-lg border border-n-alpha-3 bg-n-background px-3 py-2 text-sm text-n-slate-12"
                      :disabled="isLoadingStages || !selectedPipelineId"
                    >
                      <option value="">
                        {{ t('CONVERSATION.HEADER.KANBAN.SELECT_STAGE') }}
                      </option>
                      <option
                        v-for="stage in quickStages"
                        :key="stage.id"
                        :value="stage.id"
                      >
                        {{ stage.name }}
                      </option>
                    </select>
                  </div>

                  <Button
                    class="w-full"
                    size="sm"
                    color="teal"
                    icon="i-lucide-plus"
                    :label="t('CONVERSATION.HEADER.KANBAN.CONFIRM')"
                    :disabled="!selectedPipelineId || isAddingToPipeline"
                    :is-loading="isAddingToPipeline"
                    @click="addContactToPipeline"
                  />
                </div>
              </div>
            </aside>

            <section
              class="rounded-xl border border-n-alpha-3 bg-n-background p-3 md:col-span-8"
            >
              <div class="mb-3 flex items-center justify-between gap-2">
                <h3 class="text-sm font-semibold text-n-slate-12">
                  {{ t('CONVERSATION.HEADER.KANBAN.MEMBERSHIPS_TITLE') }}
                </h3>
                <Button
                  size="xs"
                  color="slate"
                  variant="outline"
                  icon="i-lucide-refresh-cw"
                  :label="t('CONVERSATION.SCHEDULED_MESSAGE.ACTIONS.REFRESH')"
                  :is-loading="isLoadingMemberships"
                  @click="loadMemberships"
                />
              </div>

              <div
                v-if="isLoadingMemberships"
                class="py-8 text-center text-sm text-n-slate-11"
              >
                {{ t('CONVERSATION.HEADER.KANBAN.LOADING_MEMBERSHIPS') }}
              </div>
              <div
                v-else-if="!hasMemberships"
                class="py-8 text-center text-sm text-n-slate-11"
              >
                {{ t('CONVERSATION.HEADER.KANBAN.EMPTY_MEMBERSHIPS') }}
              </div>
              <div v-else class="max-h-[68vh] space-y-2 overflow-y-auto pr-1">
                <article
                  v-for="membership in pipelineMemberships"
                  :key="membership.id"
                  class="rounded-xl border border-n-alpha-3 bg-n-solid-1 p-3"
                >
                  <div class="mb-2 flex items-center justify-between gap-2">
                    <div class="min-w-0">
                      <p class="truncate text-sm font-semibold text-n-slate-12">
                        {{ membership.pipeline?.name }}
                      </p>
                      <p class="text-xs text-n-slate-10">
                        {{
                          `${t('CONVERSATION.HEADER.KANBAN.CURRENT_STAGE')}:`
                        }}
                        {{ membership.stage?.name || '-' }}
                      </p>
                    </div>
                    <span
                      class="inline-flex items-center rounded-full bg-n-alpha-2 px-2 py-1 text-[11px] text-n-slate-11"
                    >
                      {{ `#${membership.id}` }}
                    </span>
                  </div>

                  <div class="grid grid-cols-1 gap-2 md:grid-cols-12">
                    <select
                      v-model="membershipStageSelection[membership.id]"
                      class="rounded-lg border border-n-alpha-3 bg-n-background px-3 py-2 text-sm text-n-slate-12 md:col-span-7"
                    >
                      <option
                        v-for="stage in membership.stages || []"
                        :key="stage.id"
                        :value="stage.id"
                      >
                        {{ stage.name }}
                      </option>
                    </select>
                    <Button
                      size="sm"
                      color="blue"
                      variant="outline"
                      class="md:col-span-3"
                      :label="t('CONVERSATION.HEADER.KANBAN.MOVE_STAGE')"
                      :is-loading="movingMembershipId === membership.id"
                      @click="moveMembership(membership)"
                    />
                    <Button
                      size="sm"
                      color="ruby"
                      variant="faded"
                      class="md:col-span-2"
                      :label="t('CONVERSATION.HEADER.KANBAN.REMOVE')"
                      :is-loading="removingMembershipId === membership.id"
                      @click="requestRemoveMembership(membership)"
                    />
                  </div>
                </article>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>

    <Dialog
      ref="deleteMembershipDialogRef"
      type="alert"
      :title="t('CONVERSATION.HEADER.KANBAN.REMOVE_CONFIRM.TITLE')"
      :description="t('CONVERSATION.HEADER.KANBAN.REMOVE_CONFIRM.DESCRIPTION')"
      :confirm-button-label="
        t('CONVERSATION.HEADER.KANBAN.REMOVE_CONFIRM.CONFIRM')
      "
      :is-loading="removingMembershipId === pendingRemoveMembership?.id"
      @confirm="removeMembership"
    />

    <ScheduleMessageDialog
      ref="scheduleDialogRef"
      :conversation-id="currentChat?.id"
    />
  </div>
</template>
